import { memo } from 'react';

import * as St from './index';

import { UNIT_MEASURE } from '~/core/constants';
import formatUOM from '~/core/helpers/formatUOM';
import { ColorModel } from '~/core/models';
import { formatPriceUnitOrFree, getProductDescription } from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { goToStep, selectStepState } from '~/redux/slices/stepper';
import CardSaleImg from '~atoms/CardSaleImg';
import PlainCard from '~atoms/PlainCard';
import DiscountPriceCell from '~molecules/DiscountPriceCell';
import ColorItemImage from '~organisms/ColorItemImage';

export type ColorItemData = {
  color: ColorModel;
};

export type ColorItemProps = React.AriaAttributes & {
  data: ColorItemData;
  isSelected?: boolean;
  onSelect?: () => void;
  setIsSlabViewerVisible: (isSlabViewerVisible: boolean) => void;
  setPreviewColor: (color: ColorModel) => void;
  showColorVisualizer: boolean;
  setShowColorVisualizer: (showColorVisualizer: boolean) => void;
};

const ColorItem = ({
  data,
  isSelected = false,
  onSelect,
  setIsSlabViewerVisible,
  setPreviewColor,
  showColorVisualizer,
  setShowColorVisualizer,
  ...ariaAttrs
}: ColorItemProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const step = useAppSelector(selectStepState);
  const { color } = data;
  const description = getProductDescription(color);
  const colorPrice = formatPriceUnitOrFree(
    color.price,
    formatUOM(UNIT_MEASURE.MATERIALS)
  );

  const handleViewSlabClick = (color: ColorModel) => {
    setIsSlabViewerVisible(true);
    setPreviewColor(color);
  };

  const handleColorVisualizerChange = () => {
    setShowColorVisualizer(!showColorVisualizer);
  };

  const handleDesignClick = () => {
    dispatch(goToStep(step.next));
  };

  return (
    <PlainCard
      withHover
      role="listitem"
      isSelected={isSelected}
      onClick={onSelect}
    >
      <St.Content {...ariaAttrs}>
        <ColorItemImage
          color={color}
          description={description}
          handleViewSlabClick={handleViewSlabClick}
          handleColorVisualizerChange={handleColorVisualizerChange}
          handleDesignClick={handleDesignClick}
          isSelectedColor={isSelected}
        />

        <St.ColorInfo>
          <St.ColorName>{description}</St.ColorName>
          <St.ColorPriceSection>
            {color.discountPercentage && <CardSaleImg />}
            <St.PriceContainer>
              <span>{colorPrice}</span>
              {color.discountPercentage && (
                <St.DiscountContainer>
                  <DiscountPriceCell
                    discountPercentage={color.discountPercentage}
                    priceWithoutDiscount={color.priceWithoutDiscount}
                  />
                  {/* Hidden for a while as required in EE3-700 */}
                  {/* <St.SampleLink href="#" target="_blank">
                    By sample $10.00
                  </St.SampleLink> */}
                </St.DiscountContainer>
              )}
            </St.PriceContainer>
          </St.ColorPriceSection>
        </St.ColorInfo>
      </St.Content>
    </PlainCard>
  );
};

export default memo(ColorItem);
